<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="qa-question">
        <span class="qa-bar qa-bar--xl"></span>
      </div>
      <div class="qa-list">
        <div
          v-for="(q, i) in questions"
          :key="q.text"
          class="qa-item"
          :class="{ '--top': i === 0 }"
          :style="{ '--row-i': i }"
        >
          <div class="qa-vote">
            <span class="material-symbols-rounded qa-vote-arrow">arrow_upward</span>
            <span class="qa-vote-count">{{ q.votes }}</span>
          </div>
          <div class="qa-content">
            <span class="qa-text">{{ q.text }}</span>
            <span class="qa-author">{{ q.author }}</span>
          </div>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="qa-phone-q"></span>
      <div class="qa-phone-input">
        <span class="qa-phone-typed">When does it ship?<span class="qa-caret">|</span></span>
      </div>
      <div class="qa-phone-list">
        <div class="qa-phone-row">
          <button class="qa-phone-up --voted" tabindex="-1">
            <span class="material-symbols-rounded">arrow_upward</span>
          </button>
          <span class="qa-phone-text">How will pricing change?</span>
        </div>
        <div class="qa-phone-row">
          <button class="qa-phone-up" tabindex="-1">
            <span class="material-symbols-rounded">arrow_upward</span>
          </button>
          <span class="qa-phone-text">Migration timeline?</span>
        </div>
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewQna',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      questions: [
        { text: 'When does the new pricing take effect?', author: 'Anonymous', votes: 24 },
        { text: 'Will we keep API backwards compat?', author: 'Maya', votes: 18 },
        { text: 'Hiring plans for next quarter?', author: 'Anonymous', votes: 12 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.qa-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.qa-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.qa-list { display: grid; gap: 6px; }
.qa-item {
  display: grid;
  grid-template-columns: 36px 1fr;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(15, 15, 20, 0.03);
  border: 1px solid rgba(15, 15, 20, 0.06);
}
.qa-item.--top {
  background: color-mix(in srgb, var(--accent) 10%, white);
  border-color: color-mix(in srgb, var(--accent) 30%, transparent);
  animation: qa-rise 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.qa-vote {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 1px;
  width: 36px;
  border-radius: 6px;
  padding: 4px 0;
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.08);
}
.qa-item.--top .qa-vote {
  background: var(--accent);
  border-color: var(--accent);
}
.qa-vote-arrow { font-size: 12px; color: rgba(15, 15, 20, 0.5); }
.qa-item.--top .qa-vote-arrow { color: white; }
.qa-vote-count {
  font-size: 11px; font-weight: 800;
  color: rgba(15, 15, 20, 0.7);
  font-variant-numeric: tabular-nums;
}
.qa-item.--top .qa-vote-count { color: white; }

.qa-content { display: grid; gap: 2px; min-width: 0; }
.qa-text {
  font-size: 11px; font-weight: 600;
  color: rgba(15, 15, 20, 0.78);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.qa-author {
  font-size: 9px; font-weight: 700;
  color: rgba(15, 15, 20, 0.4);
  text-transform: uppercase; letter-spacing: 0.04em;
}

/* ---- Audience phone ---- */
.qa-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.qa-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 8px;
  padding: 9px 9px;
  background: white;
}
.qa-phone-typed {
  font-size: 11px; font-weight: 600;
  color: rgba(15, 15, 20, 0.8);
}
.qa-caret { color: var(--accent); animation: qa-blink 1s steps(2) infinite; }

.qa-phone-list { display: grid; gap: 4px; }
.qa-phone-row {
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 6px;
  padding: 5px 6px;
  border-radius: 6px;
  background: rgba(15, 15, 20, 0.03);
}
.qa-phone-up {
  appearance: none; background: white;
  width: 22px; height: 22px;
  border-radius: 4px;
  border: 1px solid rgba(15, 15, 20, 0.1);
  display: grid; place-items: center;
  cursor: pointer;
  padding: 0;
}
.qa-phone-up .material-symbols-rounded {
  font-size: 12px; color: rgba(15, 15, 20, 0.5);
}
.qa-phone-up.--voted {
  background: var(--accent); border-color: var(--accent);
  animation: qa-vote 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.qa-phone-up.--voted .material-symbols-rounded { color: white; }
.qa-phone-text {
  font-size: 10px; font-weight: 600;
  color: rgba(15, 15, 20, 0.7);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

@keyframes qa-rise {
  0%, 30% { transform: translateY(8px); opacity: 0.6; }
  55%     { transform: translateY(-2px); opacity: 1; }
  70%, 100% { transform: translateY(0); opacity: 1; }
}
@keyframes qa-vote {
  0%, 60%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 40%, transparent); transform: scale(1); }
  70%       { box-shadow: 0 0 0 6px color-mix(in srgb, var(--accent) 0%, transparent); transform: scale(1.15); }
  85%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); transform: scale(1); }
}
@keyframes qa-blink { 0% { opacity: 1; } 100% { opacity: 0; } }

@media (prefers-reduced-motion: reduce) {
  .qa-item.--top, .qa-phone-up.--voted, .qa-caret { animation: none; }
}
</style>
